import { LoaderComponent, SingleTableBoxScene } from "@/shared/components/ui";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useFetchGetByIdfactibilidadCorporativos } from "@/shared/actions/erp/factibilidad_corporativo/factibilidad_corporativo.actions";
import { useFetchGetTrazoCorporativos } from "@/shared/actions/erp/trazo_corporativo";
import { ROUTER_PATHS } from "@/shared";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TablePagination,
} from "@mui/material";
import {
  ArrowBack,
  AccessTime,
  Info,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { ITrazoCorporativo } from "@/shared/interfaces/erp/factibilidad_corporativo";
import { useMemo, useState } from "react";

export default function ViewTrazabilidadFactibilidadPage() {
  const navigate = useNavigate();
  const { idfactibilidadcorporativa } = useParams();
  const [searchParams] = useSearchParams();
  ///pagination
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10);
  //pagina
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };
   //numero de elementos por pagina
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };



  // Determinar de dónde viene el usuario para el botón de regreso
  const fromPage = searchParams.get('from');
  const getBackNavigation = () => {
    switch (fromPage) {
      case 'estado-venta':
        return {
          path: ROUTER_PATHS.ventasCorporativas.estadoVentaCorporativoNav,
          label: 'Volver a Estado Venta Corporativo'
        };
      case 'seguimiento-coste':
        return {
          path: ROUTER_PATHS.ventasCorporativas.seguimientoCosteNav,
          label: 'Volver a Seguimiento Coste'
        };
      default:
        return {
          path: ROUTER_PATHS.noc.factibilidadCorporativoNav,
          label: 'Volver a Factibilidad Corporativa'
        };
    }
  };
  const backNavigation = getBackNavigation();

  // Obtener datos de la factibilidad
  const {
    data: dataFactibilidad,
    isLoading: isLoadingFactibilidad
  } = useFetchGetByIdfactibilidadCorporativos(idfactibilidadcorporativa!);

  // El modelo TrazoCorporativo usa el campo 'codigo' de la factibilidad, no el id_factibilidad_coorporativa
  const codigoFactibilidad = dataFactibilidad?.data?.codigo;
  // También obtenemos el código de la preventa para buscar trazos relacionados
  // El campo preventa_coorporativa contiene el codigo FK de la preventa
  const codigoPreventa = dataFactibilidad?.data?.preventa_coorporativa;

  // Obtener trazos de la factibilidad usando el 'codigo'
  const {
    data: dataTrazosFactibilidad,
    isLoading: isLoadingTrazosFactibilidad
  } = useFetchGetTrazoCorporativos(
    {
      factibilidad_corporativa: codigoFactibilidad,
    } as ITrazoCorporativo,
    !!codigoFactibilidad
  );

  // Obtener trazos de la preventa para mostrar el historial completo
  const {
    data: dataTrazosPreventa,
    isLoading: isLoadingTrazosPreventa
  } = useFetchGetTrazoCorporativos(
    {
      preventa_corporativa: codigoPreventa,
    } as ITrazoCorporativo,
    !!codigoPreventa
  );

  // Combinar y ordenar todos los trazos
  const trazosOrdenados = useMemo(() => {
    const trazosFactibilidad = dataTrazosFactibilidad?.data?.data || [];
    const trazosPreventa = dataTrazosPreventa?.data?.data || [];

    // Combinar ambos arrays y eliminar duplicados por id_trazo_corporativo
    const todosLosTrazos = [...trazosFactibilidad, ...trazosPreventa];
    const trazosUnicos = todosLosTrazos.reduce((acc, trazo) => {
      if (!acc.find(t => t.id_trazo_corporativo === trazo.id_trazo_corporativo)) {
        acc.push(trazo);
      }
      return acc;
    }, [] as ITrazoCorporativo[]);

    // Ordenar por fecha de creación (más antiguo primero)
    return trazosUnicos.sort((a, b) =>
      new Date(a.fecha_creacion || 0).getTime() - new Date(b.fecha_creacion || 0).getTime()
    );
  }, [dataTrazosFactibilidad, dataTrazosPreventa]);

  //  Paginación de los trazos
  const trazosPaginados = trazosOrdenados.slice(
    page * pageSize,
    page * pageSize + pageSize
  )
  console.log('trazos_paginados', trazosPaginados)
  // Fin de la función principal
  if (isLoadingFactibilidad || isLoadingTrazosFactibilidad || isLoadingTrazosPreventa) return <LoaderComponent />;

  if (!dataFactibilidad) return null;

  const factibilidad = dataFactibilidad.data;


  return (
    <SingleTableBoxScene
      title="Trazabilidad de Factibilidad Corporativa"
      visibleReport={false}
    >
      <Box sx={{ p: 2 }}>
        {/* Botón de regreso */}
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          //onClick={() => navigate(backNavigation.path)}
          onClick={() => {
            navigate({
              pathname: backNavigation.path,
              search: fromPage ? `?from=${fromPage}` : ''
            });
          }}
          sx={{ mb: 3 }}
        >
          {backNavigation.label}
        </Button>

        {/* Información de la Factibilidad */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Info color="primary" />
              Información de la Factibilidad
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Número de Referencia
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {factibilidad.numero_referencia || "N/A"}
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Razón Social
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {factibilidad.prospecto_corporativo_data?.razon_social || "N/A"}
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Identificación
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {factibilidad.prospecto_corporativo_data?.identificacion || "N/A"}
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Estado Factibilidad
                </Typography>
                <Chip
                  label={factibilidad.estado_factibilidad || "N/A"}
                  color={factibilidad.estado_factibilidad === "APROBADO" ? "success" : "default"}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Usuario Aprobador
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {factibilidad.user_aprueba || "N/A"}
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Fecha de Aprobación
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {factibilidad.fecha_factibilidad
                    ? dayjs(factibilidad.fecha_factibilidad).format("DD/MM/YYYY HH:mm")
                    : "N/A"}
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Aprobado por Inventario
                </Typography>
                <Chip
                  label={factibilidad.aprobado_inventario ? "SÍ" : "NO"}
                  color={factibilidad.aprobado_inventario ? "success" : "warning"}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Aprobado por Supervisor
                </Typography>
                <Chip
                  label={factibilidad.aprobado_supervisor ? "SÍ" : "NO"}
                  color={factibilidad.aprobado_supervisor ? "success" : "warning"}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Tipo
                </Typography>
                <Chip
                  label={factibilidad.es_backup ? "BACKUP" : "PRINCIPAL"}
                  color={factibilidad.es_backup ? "warning" : "primary"}
                  size="small"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Tabla de Trazabilidad */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTime color="primary" />
              Historial de Acciones ({trazosOrdenados.length} registros)
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {trazosOrdenados.length === 0 ? (
              <Typography variant="body1" color="text.secondary" textAlign="center" py={4}>
                No hay registros de trazabilidad para esta factibilidad.
              </Typography>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small" sx={{ minWidth: 1100 }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'primary.main' }}>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold', width: 50 }}>#</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Acción</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 250 }}>Detalles</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Usuario</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Rol</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Tipo Empleado</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>IP</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Fecha y Hora</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* {trazosOrdenados.map((trazo, index) => */}
                      {trazosPaginados.map((trazo, index) => {
                        console.log('Trazo',trazo)
                        const isLast = index === trazosOrdenados.length - 1;
                        // Solo mostrar verde si es el último registro Y la acción es aprobación final Y el estado es APROBADO
                        const isApproved = isLast &&
                          trazo.accion === 'CAMBIO DE ESTADO A APROBADO' &&
                          factibilidad.estado_factibilidad === 'APROBADO';
                        return (
                          <TableRow
                            key={trazo.id_trazo_corporativo || index}
                            sx={{
                              '&:nth-of-type(odd)': { backgroundColor: 'action.hover' },
                              '&:hover': { backgroundColor: 'action.selected' },
                              ...(isApproved && {
                                backgroundColor: '#e8f5e9',
                                borderLeft: '4px solid #4caf50',
                                '&:hover': { backgroundColor: '#c8e6c9' },
                              }),
                            }}
                          >
                            <TableCell>
                              <Chip
                                //label={index + 1}
                                label={(page * pageSize) + index + 1}
                                size="small"
                                color={isApproved ? "success" : "primary"}
                                sx={{ minWidth: 32 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                fontWeight="bold"
                                color={isApproved ? "success.dark" : "primary.dark"}
                              >
                                {trazo.accion || "N/A"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontSize={11} color="text.secondary">
                                {trazo.detalles || "—"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {trazo.user || "N/A"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {trazo.rol_usuario ? (
                                <Chip
                                  label={trazo.rol_usuario}
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                />
                              ) : "N/A"}
                            </TableCell>
                            <TableCell>
                              {trazo.tipo_empleado ? (
                                <Chip
                                  label={trazo.tipo_empleado}
                                  size="small"
                                  variant="outlined"
                                  color="info"
                                />
                              ) : "N/A"}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontFamily="monospace" fontSize={12}>
                                {trazo.ip || "N/A"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {trazo.fecha_creacion
                                  ? dayjs(trazo.fecha_creacion).format("DD/MM/YYYY HH:mm:ss")
                                  : "N/A"}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                {/* Paginación */}
                <TablePagination
                  component="div"
                  count={trazosOrdenados.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={pageSize}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  labelRowsPerPage="Registros por página:"
                />
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </SingleTableBoxScene>
  );
}
